import React from 'react';
import {
    Dialog,
    DialogContent,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import CloseIcon from "@mui/icons-material/Close";
import { useFormik, FormikProvider, Form } from 'formik';
import DialogActions from '@mui/material/DialogActions';
import { LoadingButton } from '@mui/lab';
import { useSelector } from 'react-redux';
import { LocationAddress } from '../../types';
import { RootState } from '../../store';
import FormBuilder from '../form-builder';
import { ADMIN_LOCATION_ADDRESS_INPUTS } from '../../builders/admins-builders';
import { AuthService } from '../../services/auth.service';
import { toast } from 'react-toastify';

interface Props {
    visible: boolean,
    type: 'home' | 'work',
    onClose(): void,
};

export const LocationForm: React.FC<Props> = ({
    visible,
    type,
    onClose,
}) => {
    const { authUser } = useSelector((state: RootState) => state.auth);
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

    const title = type === 'home' ?
        'Change Home Location' : 
        'Change Work Location';

    const initialValues: LocationAddress = type === 'home' ? 
        authUser?.homeLocation ?? {} as LocationAddress :
        authUser?.workLocation ?? {} as LocationAddress;

        
    const formik = useFormik<LocationAddress>({
        initialValues,
        onSubmit: async (values) => {
            await AuthService.changeLocation(values, type, (isValid, msg) => {
                if (!isValid) {
                    toast(
                        msg, 
                        {
                            autoClose: 1500,
                            type: 'error',
                        }
                    );
                    return;
                }

                formik.resetForm();

                toast(
                    msg, 
                    {
                        autoClose: 1500,
                        type: 'success',
                    }
                );

                onClose();
            });
        }
    });

    const { handleSubmit, errors, touched, isSubmitting, getFieldProps } = formik;

    return (
        <Dialog
            open={visible}
            maxWidth={'lg'}
            scroll={'paper'}
            fullWidth={isSmallScreen}
        >
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Stack direction={'row'} justifyContent={'space-between'}>
                    <Typography sx={{ ml: 3, mb: 2, mt: 2 }} fontSize={18} fontWeight={700} color={'text.default'}>
                        {title}
                    </Typography>
                    <Tooltip title="Close form">
                    <IconButton
                        onClick={onClose}
                        component="span"
                        sx={{ mr: 2, height: 30, width: 30, alignSelf: 'center' }}
                    >
                        <CloseIcon />
                    </IconButton>
                    </Tooltip>
                </Stack>
            </Box>
            <FormikProvider value={formik}>
                <Form noValidate onSubmit={handleSubmit}>
                    <DialogContent sx={{ maxWidth: 800, minWidth: 500 }} dividers>
                        <FormBuilder
                            size="medium"
                            errors={errors}
                            getFieldProps={getFieldProps}
                            touched={touched}
                            inputs={[...ADMIN_LOCATION_ADDRESS_INPUTS]}
                        />
                    </DialogContent>
                    <DialogActions>
                        <LoadingButton
                            variant="contained"
                            type="submit"
                            loading={isSubmitting}
                            fullWidth={isSmallScreen}
                        >
                            Change Address
                        </LoadingButton>
                    </DialogActions>
                </Form>
            </FormikProvider>
        </Dialog>
    )
};